import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Download, Car, Droplets, DollarSign, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { API, authFetch } from '../utils/api';

const LOCATIONS = {
  'osf': 'OSF',
  'civic_center': 'Civic Center',
  'civic_center_west': 'Civic Center West',
  'great_park': 'Great Park'
};

const WASH_TYPES = {
  'wash_only_doors_locked': 'Wash Only Doors Locked',
  'interior_and_wash': 'Interior & Wash',
  'wash_only': 'Wash Only',
  'interior_only': 'Interior Only',
  'not_done': 'Not Done'
};

export default function Analytics({ user }) {
  const [history, setHistory] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(fetchAllData, 30000);
    const onFocus = () => fetchAllData();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, [location.key]);

  const fetchAllData = async () => {
    try {
      const [historyRes, vehiclesRes, groupsRes] = await Promise.all([
        authFetch(`${API}/history`),
        authFetch(`${API}/vehicles`),
        authFetch(`${API}/vehicle-groups`)
      ]);

      if (historyRes.ok) setHistory(await historyRes.json());
      if (vehiclesRes.ok) setVehicles(await vehiclesRes.json());
      if (groupsRes.ok) setGroups(await groupsRes.json());
    } catch (error) {
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const response = await authFetch(`${API}/export/history`, {
      });
      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'analytics_report.xlsx';
      a.click();
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-full">
          <p className="text-slate-500">Loading analytics...</p>
        </div>
      </Layout>
    );
  }

  // Helper to get price/cost from vehicle's group
  const getVehiclePriceCost = (vehicle) => {
    if (!vehicle) return { price: 0, cost: 0 };
    if (vehicle.group_id) {
      const group = groups.find(g => g.group_id === vehicle.group_id);
      if (group) {
        return { price: group.price || 0, cost: group.cost || 0 };
      }
    }
    // Fallback to vehicle's own price/cost (legacy data)
    return { price: vehicle.price || 0, cost: vehicle.cost || 0 };
  };

  // Calculate total price and cost from wash history vehicles using group prices
  const calculateTotals = () => {
    let totalPrice = 0;
    let totalCost = 0;
    
    history.forEach(wash => {
      const vehicle = vehicles.find(v => v.vehicle_id === wash.vehicle_id);
      const { price, cost } = getVehiclePriceCost(vehicle);
      totalPrice += price;
      totalCost += cost;
    });
    
    return { totalPrice, totalCost };
  };

  const { totalPrice, totalCost } = calculateTotals();

  const getVehicleInfo = (vehicleId) => {
    const vehicle = vehicles.find(v => v.vehicle_id === vehicleId);
    if (!vehicle) return { display: vehicleId, details: '' };
    return {
      display: vehicle.unit_number || '-',
      details: `${vehicle.make || ''} ${vehicle.model || ''}`.trim()
    };
  };

  const getLocationLabel = (value) => LOCATIONS[value] || value || '-';
  const getWashTypeLabel = (value) => WASH_TYPES[value] || value || '-';

  const recentWashes = history;

  const stats = [
    { 
      name: 'Total Price', 
      value: `$${totalPrice.toFixed(2)}`, 
      icon: DollarSign, 
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-50',
    },
    { 
      name: 'Total Cost', 
      value: `$${totalCost.toFixed(2)}`, 
      icon: DollarSign, 
      color: 'text-slate-500',
      bgColor: 'bg-slate-100',
    },
    { 
      name: 'Total Washes', 
      value: history.length || 0, 
      icon: Droplets, 
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
    },
    { 
      name: 'Fleet Size', 
      value: vehicles.length || 0, 
      icon: Car, 
      color: 'text-sky-500',
      bgColor: 'bg-sky-50',
    },
  ];

  return (
    <Layout user={user}>
      <div className="p-4 lg:p-12 space-y-6 lg:space-y-8 bg-slate-200 min-h-screen">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-5xl font-heading font-bold tracking-tight" data-testid="analytics-title">Analytics</h1>
            <p className="text-slate-600 text-sm lg:text-base mt-1 lg:mt-2">Detailed insights and reports</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handlePrint} data-testid="print-report-button" className="text-xs lg:text-sm">
              <Printer className="h-4 w-4 mr-1 lg:mr-2" />
              PRINT
            </Button>
            <Button variant="outline" onClick={handleExportReport} data-testid="export-report-button" className="text-xs lg:text-sm">
              <Download className="h-4 w-4 mr-1 lg:mr-2" />
              EXPORT
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="border border-slate-200 bg-white" data-testid={`analytics-stat-${index}`}>
                <CardContent className="p-4 lg:p-6">
                  <div className="flex items-center justify-between">
                    <div className={`p-2 lg:p-3 rounded-lg ${stat.bgColor}`}>
                      <Icon className={`h-5 w-5 lg:h-6 lg:w-6 ${stat.color}`} strokeWidth={1.5} />
                    </div>
                  </div>
                  <div className="mt-3 lg:mt-4">
                    <p className="text-xs lg:text-sm font-semibold uppercase tracking-wide text-slate-600">{stat.name}</p>
                    <p className="text-xl lg:text-3xl font-heading font-bold tracking-tight mt-1">{stat.value}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Recent Washes */}
        <Card className="border border-slate-200 bg-white">
          <CardHeader>
            <CardTitle className="font-heading text-xl uppercase tracking-tight">Recent Washes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentWashes.length > 0 ? (
                recentWashes.map((wash, index) => {
                  const vehicleInfo = getVehicleInfo(wash.vehicle_id);
                  const vehicle = vehicles.find(v => v.vehicle_id === wash.vehicle_id);
                  const { price, cost } = getVehiclePriceCost(vehicle);
                  return (
                    <div key={wash.history_id || index} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0" data-testid={`analytics-wash-${index}`}>
                      <div>
                        <p className="font-semibold font-mono text-sm">{vehicleInfo.display}</p>
                        <p className="text-sm text-slate-500">{vehicleInfo.details}</p>
                        <div className="flex gap-2 mt-1">
                          <span className="text-xs text-emerald-600 font-medium">Price: ${price.toFixed(2)}</span>
                          <span className="text-xs text-slate-600 font-medium">Cost: ${cost.toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
                          wash.wash_type === 'not_done' 
                            ? 'bg-red-50 text-red-700 border-red-200'
                            : wash.wash_type === 'interior_and_wash'
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : 'bg-slate-50 text-slate-700 border-slate-200'
                        }`}>
                          {getWashTypeLabel(wash.wash_type)}
                        </span>
                        <p className="text-xs mt-1">
                          <span className="inline-flex items-center px-2 py-0.5 rounded font-medium bg-blue-50 text-blue-700 border border-blue-200">
                            {getLocationLabel(wash.location)}
                          </span>
                        </p>
                        <p className="text-xs text-slate-500 mt-1">{new Date(wash.wash_date).toLocaleDateString()}</p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-slate-500 text-center py-8">No recent washes</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
